import useTreeSelectCtrl from '@/shared/Tree/ctrl/useTreeSelectCtrl.ts'
import { useMemo, useState } from 'react'
import { ITreeGroup } from '@/shared/Tree/tree.types.ts'
import useTreeExpandCtrl from '@/shared/Tree/ctrl/useTreeExpandCtrl.ts'
import confirmService from '@/services/ConfirmService/confirm.service.ts'
import css from './passportSchemaGroupTree.module.css'
import Tree from '@/shared/Tree'
import CreateEntityButton from '@/shared/Buttons/CreateEntityButton'
import { IPassportGroup, IPassportSchema } from '@/pages/MDM/mdm.types.ts'
import mdmApi from '@/pages/MDM/mdm.api.ts'
import mdmEvents from '@/pages/MDM/events/mdm.events.ts'
import CreatePassportGroupModal from '@/pages/MDM/components/Passports/CreatePassportGroupModal'
import DefaultTreeGroup from '@/shared/Tree/ui/DefaultTreeGroup'
import MenuOptionEdit from '@/shared/Dropdowns/DropdownListItem/DropdownListItemEdit'
import MenuOptionDelete from '@/shared/Dropdowns/DropdownListItem/DropdownListItemDelete'
import defaultTreeGroupStyling from '@/shared/Tree/ui/DefaultTreeGroup/defaultTreeGroup.styling.ts'
import UpdatePassportGroupModal from '@/pages/MDM/components/Passports/UpdatePassportGroupModal'

interface IProps {
  selectCtrl: ReturnType<typeof useTreeSelectCtrl>
  passportSchema: IPassportSchema
}

const PassportSchemaGroupsTree = (props: IProps) => {
  const [createGroupInitialParentId, setCreateGroupInitialParentId] = useState<IPassportGroup['id']>(null)
  const [createGroupModalOpen, setCreateGroupModalOpen] = useState(false)

  const [updateGroupModalOpen, setUpdateGroupModalOpen] = useState(false)
  const [updateGroupModalId, setUpdateGroupModalId] = useState<IPassportGroup['id']>(null)

  const [isOpenExpand, setIsOpenExpand] = useState(false)
  const [clickedOnExpand, setClickedOnExpand] = useState(false)

  const treeGroups: ITreeGroup<IPassportGroup, number>[] = useMemo(() => {
    if (!props.passportSchema.groups.length) return []

    return props.passportSchema.groups.map((g) => ({ id: g.id, parentId: g.parentId, data: g, type: 'list-values' }))
  }, [props.passportSchema.groups])

  const expandCtrl = useTreeExpandCtrl(
    `/mdm/edit-passport/${props.passportSchema.id}`,
    isOpenExpand,
    treeGroups,
    [],
    setIsOpenExpand,
    clickedOnExpand,
    setClickedOnExpand
  )

  const handleDelete = (group: IPassportGroup) => {
    confirmService.requestConfirm({
      buttonType: 'primary',
      description: `Вы действительно хотите удалить группу "${group.name}"?`,
      onConfirm: () =>
        mdmApi.deletePassportSchemaGroup(group.id).then((r) => {
          if (r.status === 'success') {
            mdmEvents.passportSchemaGroupDeleted(r.body.id)
          }
        }),
    })
  }

  const renderGroup = (group: ITreeGroup<IPassportGroup, number>) => {
    const menuContent = (
      <>
        <MenuOptionEdit
          text="Редактировать"
          onClick={() => {
            setUpdateGroupModalId(group.id)
            setUpdateGroupModalOpen(true)
          }}
        />
        <MenuOptionDelete text="Удалить" onClick={() => handleDelete(group.data)} />
      </>
    )

    return (
      <DefaultTreeGroup
        key={group.id}
        title={group.data.name}
        group={group.data}
        threeDotsMenuContent={menuContent}
        onCreate={() => {
          setCreateGroupInitialParentId(group.id)
          setCreateGroupModalOpen(true)
        }}
      />
    )
  }

  return (
    <div className={css.wrapperTree}>
      <div>
        <Tree
          leafs={[]}
          groups={treeGroups}
          renderGroup={renderGroup}
          renderLeaf={null}
          styling={{ groupClassname: defaultTreeGroupStyling.groupClassname }}
          expandCtrl={expandCtrl}
          selectCtrl={props.selectCtrl}
          onClickGroup={(group: Record<string, any>) => props.selectCtrl.handleSelect(group.id)}
        />
      </div>

      <div className={css.btnCreateGroup}>
        <CreateEntityButton
          fullWidth={true}
          type="default"
          onClick={() => setCreateGroupModalOpen(true)}
          text="Создать группу"
        />
      </div>
      <CreatePassportGroupModal
        initialGroupId={createGroupInitialParentId}
        passportId={props.passportSchema.id}
        open={createGroupModalOpen}
        onClose={() => setCreateGroupModalOpen(false)}
      />
      <UpdatePassportGroupModal
        groupId={updateGroupModalId}
        passportSchemaId={props.passportSchema.id}
        open={updateGroupModalOpen}
        onClose={() => setUpdateGroupModalOpen(false)}
      />
    </div>
  )
}

export default PassportSchemaGroupsTree
